import { Icon } from '@ipfabric/ui-components/Icon/Icon';
import type { CloudResourceHierarchyPathItem } from '@ipfabric/utils/types/CloudResourceHierarchyPathItem';
import { useLocale } from 'containers/App/LocaleContext';
import { LoaderAndText } from 'containers/LoaderAndText/LoaderAndText';
import { type FC, Fragment, memo, useEffect, useState } from 'react';
import { useApi } from 'utils/request/useApi';
import type { DeviceViewNodeEntity } from '../deviceViewTypes';
import { getCloudConstructKindFromNode } from '../helpers/cloudConstruct';
import {
  type CloudResourceHierarchyPathResponse,
  getCloudResourceHierarchyPathRequestPayload,
} from './getCloudResourceHierarchyPathRequestPayload';

/** Fixed dimensions of each hierarchy-path box (type line + 2 value rows). */
const NODE_WIDTH_PX = 220;
const NODE_HEIGHT_PX = 98;

/**
 * User-facing labels for the known `vCloudResourceHierarchy.type` values.
 * Falls back to the raw DB value when a type is not listed here.
 */
const HIERARCHY_TYPE_LABELS: Record<string, string> = {
  folder: 'Folder',
  account: 'Account',
  managementGroup: 'Management Group',
  organizationRoot: 'Organization Root',
  organization: 'Organization',
  subscription: 'Subscription',
  tenant: 'Tenant',
  organizationalUnit: 'Organizational Unit',
  project: 'Project',
  resourceGroup: 'Resource Group',
};

const getTypeLabel = (type: string | null | undefined): string =>
  type ? (HIERARCHY_TYPE_LABELS[type] ?? type) : '';

/**
 * A single hierarchy-path box: the node type on the first line and the node
 * name (preferred) or id in bold on the second line.
 */
const HierarchyPathNode: FC<{ item: CloudResourceHierarchyPathItem }> = ({
  item,
}) => (
  <div
    className='flex flex-col overflow-hidden rounded border-2 border-[#c1c2c7] border-solid bg-white px-3 py-2 leading-tight gap-xs'
    style={{ width: NODE_WIDTH_PX, height: NODE_HEIGHT_PX }}
  >
    <span className='truncate text-gray-500 text-base'>
      {getTypeLabel(item.type)}
    </span>
    <span className='line-clamp-2 text-sm font-semibold'>
      {item.name ?? item.id}
    </span>
  </div>
);

type LoadState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'loaded'; path: CloudResourceHierarchyPathItem[] };

interface Props {
  active: boolean;
  deviceViewEntity: DeviceViewNodeEntity;
  snapshotId: string | null;
}

const BareCloudResourceHierarchyTabContent: FC<Props> = (props) => {
  const msg = useLocale();
  const api = useApi();
  const kind = getCloudConstructKindFromNode(
    props.deviceViewEntity.nodeType,
    props.deviceViewEntity.extra,
  );
  const id = props.deviceViewEntity.sn;
  const snapshotId = props.snapshotId;
  const [visited, setVisited] = useState<boolean>(false);
  const [state, setState] = useState<LoadState>({ status: 'loading' });

  // biome-ignore lint/correctness/useExhaustiveDependencies(visited): to be triggered only by activation
  useEffect(() => {
    if (!visited && props.active) {
      setVisited(true);
    }
  }, [props.active]);

  useEffect(() => {
    if (!visited) {
      return;
    }
    if (kind === null || !id || !snapshotId) {
      setState({ status: 'loaded', path: [] });
      return;
    }
    const abortController = new AbortController();
    setState({ status: 'loading' });
    (async () => {
      try {
        const result =
          await api.abortableRequest<CloudResourceHierarchyPathResponse>({
            ...getCloudResourceHierarchyPathRequestPayload({
              kind,
              id,
              snapshotId,
            }),
            abortSignal: abortController.signal,
          });
        if (result.aborted) {
          return;
        }
        setState({ status: 'loaded', path: result.data ?? [] });
      } catch {
        if (!abortController.signal.aborted) {
          setState({ status: 'error' });
        }
      }
    })();
    return () => abortController.abort();
    // `api` is a stable, memoized reference from useApi(), so it is omitted.
  }, [visited, kind, id, snapshotId]);

  if (!visited) {
    return null;
  }

  if (state.status === 'loading') {
    return (
      <div className='flex flex-1 flex-col justify-center'>
        <LoaderAndText />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <h4>{msg.deviceView.unableToLoadData}</h4>
      </div>
    );
  }

  if (state.path.length === 0) {
    return (
      <div className='flex flex-1 items-center justify-center'>
        <h4>{msg.deviceView.resourceHierarchyEmpty}</h4>
      </div>
    );
  }

  // Root → leaf chain, aligned left-to-right with a chevron between boxes.
  // White card on the gray tab background, mirroring the "Detail" tab.
  return (
    <div className='box box-solid mb-0 flex flex-1 flex-col'>
      <div className='flex flex-row flex-wrap items-center gap-2 overflow-auto p-3'>
        {state.path.map((item, index) => (
          <Fragment key={item.id}>
            {index > 0 && (
              <Icon glyph='fa-chevron-right' className='text-black text-xl' />
            )}
            <HierarchyPathNode item={item} />
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export const CloudResourceHierarchyTabContent = memo(
  BareCloudResourceHierarchyTabContent,
);
